import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API from '../../config/API';
import CatalogueTopNav from '../new-service/CatalogueTopNav';
import SlidePanel from '../shared/SlidePanel';
import ColourPicker from '../shared/ColourPicker';
import ShiftTimePicker from '../Diary/ShiftTimePicker';
import { compressImage, isHeicFile } from '../shared/imageCompress';
import toast from '../shared/Toast';
import '../shared/ui.css'; // beu-icon-btn, beu-section-label
import '../new-service/ServiceEditorPanel.css'; // sep- editor furniture
import '../new-service/ServiceDetailPanel.css'; // sdp- read-only detail furniture
import '../gift-cards-v2/GiftCardsV2.css'; // v2 page header chrome (same as Vouchers / Transfers)
import '../settings/SettingsModern.css'; // settings-card + inline buttons
import './Resources.css';

// Catalogue → Resources — rooms, basins, chairs, machines (docs/resources.md §5).
// Same interaction language as Products/Services: a list page, and SlidePanel
// editors that slide in on desktop and up on mobile. Owner-only writes,
// enforced server-side.

// The house switch — ServiceEditorPanel defines it privately, so a local
// copy; classes come from ServiceEditorPanel.css which we already import.
const Toggle = ({ checked, onChange }) => (
    <button type="button" className={`sep-toggle ${checked ? 'sep-toggle--on' : ''}`}
        onClick={() => onChange(!checked)}>
        <span className="sep-toggle-knob" />
    </button>
);

const authHeaders = () => ({
    'Content-Type': 'application/json',
    accessToken: localStorage.getItem('accessToken'),
});

const DEFAULT_COLOUR = '#c8eeee'; // same default family as the service editor
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const DEFAULT_DAYS = () => DAYS.reduce((a, [k]) => ({ ...a, [k]: { open: k !== 'sun', start: 540, end: 1080 } }), {});
const singularOf = (name) => (name || 'unit').replace(/s$/, '');
const RESET_STEP = 5; // reset time is always a multiple of five minutes
const RESET_MAX = 240;
// Legacy values off the grid round to the nearest step, but never down to none.
const snapReset = (n) => { const v = Number(n) || 0; if (v <= 0) return 0; return Math.min(RESET_MAX, Math.max(RESET_STEP, Math.round(v / RESET_STEP) * RESET_STEP)); };
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const minToDate = (m) => { const d = new Date(); d.setHours(Math.floor(m / 60), m % 60, 0, 0); return d; };
const dateToMin = (d) => d.getHours() * 60 + d.getMinutes();

export default function ResourcesPage(props) {
    const [groups, setGroups] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    // One panel at a time. Tapping a thing opens its read-only view first
    // (the ServiceDetailPanel pattern); Edit swaps the same sheet for the editor.
    // {kind:'groupView', groupId} | {kind:'group', groupId?} |
    // {kind:'unitView', unitId} | {kind:'unit', groupId, unitId?} |
    // {kind:'removeGroup', group, affected} | {kind:'removeUnit', unit, count}
    const [panel, setPanel] = useState(null);
    // Views and editors resolve their record from state at render time, so a
    // save (which reloads) shows fresh data without re-opening the panel.
    const groupById = (id) => groups.find(g => String(g._id) === String(id)) || null;
    const unitById = (id) => units.find(u => String(u._id) === String(id)) || null;
    const afterSave = (view) => { if (window.innerWidth < 768) setPanel(null); else setPanel(view); };

    const load = useCallback(async () => {
        try {
            const res = await axios.get(API.RESOURCE_LIST, { headers: authHeaders() });
            const data = res?.data?.responseData || {};
            setGroups(data.groups || []);
            setUnits(data.resources || []);
        } catch (e) {
            toast.error('Could not load resources');
        }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const call = async (method, url, body) => {
        try {
            // FormData must set its OWN Content-Type (with the boundary) — a
            // forced application/json header kills the multipart body. This
            // exact bug took out photo uploads on 2026-05-27; not again.
            const headers = body instanceof FormData
                ? { accessToken: localStorage.getItem('accessToken') }
                : authHeaders();
            const res = await axios({ method, url, data: body, headers });
            if (res?.data?.statusCode === 1) { await load(); return res.data.responseData; }
            toast.error(res?.data?.error?.responseMessage || "That didn't save");
        } catch (e) {
            toast.error(e?.response?.data?.error?.responseMessage || "That didn't save");
        }
        return null;
    };

    const askRemoveGroup = async (g) => {
        // Dry ask first — the BE names the blast radius (§5a) before anything moves.
        const res = await call('delete', `${API.RESOURCE_GROUP}/${g._id}`);
        if (res && res.needsConfirm) setPanel({ kind: 'removeGroup', group: g, affected: res.affectedServices });
        else if (res) { toast.success(`${g.name} removed`); setPanel(null); }
    };

    const unitsOf = (g) => units.filter(u => String(u.groupId) === String(g._id));

    return (
        <div className="h_n catalogue-no-header">
            <div className="body_container cm_bg_white">
                <CatalogueTopNav {...props}>
                    <button type="button" className="catalogue-add-btn" onClick={() => setPanel({ kind: 'group' })}>Add group</button>
                </CatalogueTopNav>
                <div className="catalogue-page-wrapper">
                    <div className="catalogue-content-container">
                        <div className="catalogue-content-wrapper voucher-v2 res-page">
                            <div className="v2-page-header">
                                <div className="v2-page-title-row"><h1 className="v2-page-title">Resources</h1></div>
                                <p className="v2-page-sub">
                                    Rooms, basins, chairs and machines your services need besides the stylist.
                                    Point a service at a group from its editor, and online booking only offers
                                    times when one is actually free.
                                </p>
                            </div>

                            <div className="v2-page">
                            {loading ? (
                                <div className="settings-card"><p className="bu-hint">Loading…</p></div>
                            ) : (
                                <>
                                    {groups.length === 0 && (
                                        <div className="settings-card">
                                            <div className="ml-empty">
                                                <p className="ml-empty-title">No resources yet</p>
                                                <p className="ml-empty-sub">Start with a group, for example Colour rooms, Basins or Treatment rooms, then add each room or chair inside it.</p>
                                                <button type="button" className="bu-inline-save res-empty-btn" onClick={() => setPanel({ kind: 'group' })}>Add your first group</button>
                                            </div>
                                        </div>
                                    )}

                                    {groups.map(g => {
                                        const singular = g.name.replace(/s$/, '');
                                        const units = unitsOf(g);
                                        return (
                                        <section className="settings-card res-group" key={g._id}>
                                            <div className="settings-card-header res-card-header">
                                                {/* The group itself opens in a panel, like a unit row: rename or remove it there. */}
                                                <button type="button" className="res-group-head" onClick={() => setPanel({ kind: 'groupView', groupId: g._id })}>
                                                    <span className="res-group-head-text">
                                                        <h2>{g.name}</h2>
                                                        <p>{units.length ? `${units.length} ${units.length === 1 ? singular.toLowerCase() : g.name.toLowerCase()}` : `No ${g.name.toLowerCase()} yet`}</p>
                                                    </span>
                                                    <span className="res-row-chevron" aria-hidden="true">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                                                    </span>
                                                </button>
                                                <div className="res-card-actions">
                                                    <button type="button" className="bu-inline-save" onClick={() => setPanel({ kind: 'unit', groupId: g._id, groupName: g.name })}>Add {singular.toLowerCase()}</button>
                                                </div>
                                            </div>

                                            {units.length > 0 && (
                                                <div className="res-rows">
                                                    {units.map(u => (
                                                        <button type="button" className={`res-row${u.isActive ? '' : ' res-row--off'}`} key={u._id}
                                                            onClick={() => setPanel({ kind: 'unitView', unitId: u._id })}>
                                                            {u.images?.[0]?.url
                                                                ? <img className="res-row-thumb" src={u.images[0].url} alt="" />
                                                                : <span className="res-row-dot" style={{ background: u.diaryColour || '#8B8B93' }} />}
                                                            <span className="res-row-text">
                                                                <span className="res-row-name">{u.name}</span>
                                                                <span className="res-row-meta">
                                                                    {u.turnoverTime > 0 ? `${u.turnoverTime} min reset time` : 'No reset time'}
                                                                    {u.hours?.mode === 'custom' && ' · Set hours'}
                                                                    {!u.isActive && ' · Hidden from new bookings'}
                                                                </span>
                                                            </span>
                                                            <span className="res-row-chevron" aria-hidden="true">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {units.length === 0 && (
                                                <p className="res-rows-empty">Add the first {singular.toLowerCase()} to start offering it to bookings.</p>
                                            )}
                                        </section>
                                        );
                                    })}
                                </>
                            )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {panel?.kind === 'groupView' && groupById(panel.groupId) && (
                <GroupViewPanel group={groupById(panel.groupId)} units={unitsOf(groupById(panel.groupId))}
                    onClose={() => setPanel(null)}
                    onEdit={() => setPanel({ kind: 'group', groupId: panel.groupId })}
                    onDelete={() => askRemoveGroup(groupById(panel.groupId))}
                    onOpenUnit={(u) => setPanel({ kind: 'unitView', unitId: u._id })}
                    onAddUnit={() => setPanel({ kind: 'unit', groupId: panel.groupId })} />
            )}
            {panel?.kind === 'group' && (
                <GroupPanel group={panel.groupId ? groupById(panel.groupId) : null} onClose={() => setPanel(null)}
                    onSave={async (name) => {
                        const ok = panel.groupId
                            ? await call('put', `${API.RESOURCE_GROUP}/${panel.groupId}`, { name })
                            : await call('post', API.RESOURCE_GROUP, { name });
                        if (!ok) return;
                        toast.success(panel.groupId ? 'Saved' : `${name} added`);
                        if (panel.groupId) afterSave({ kind: 'groupView', groupId: panel.groupId }); else setPanel(null);
                    }} />
            )}
            {panel?.kind === 'unitView' && unitById(panel.unitId) && (
                <UnitViewPanel unit={unitById(panel.unitId)} group={groupById(unitById(panel.unitId).groupId)}
                    onClose={() => setPanel(null)}
                    onEdit={() => setPanel({ kind: 'unit', groupId: unitById(panel.unitId).groupId, unitId: panel.unitId })}
                    onDelete={async () => {
                        const u = unitById(panel.unitId);
                        // Dry ask: the BE reports upcoming bookings before anything moves.
                        const res = await call('delete', `${API.RESOURCE_UNIT}/${u._id}`);
                        if (res && res.needsConfirm) setPanel({ kind: 'removeUnit', unit: u, count: res.upcomingBookings });
                        else if (res) { toast.success(`${u.name} removed`); setPanel(null); }
                    }} />
            )}
            {panel?.kind === 'unit' && (
                <UnitPanel groupName={groupById(panel.groupId)?.name} unit={panel.unitId ? unitById(panel.unitId) : null} onClose={() => setPanel(null)}
                    onSave={async (draft) => {
                        // Multipart always: hours ride as a JSON string (the
                        // product options/variants pattern) and the photo, when
                        // picked, as the 'image' file part.
                        const fd = new FormData();
                        fd.append('name', draft.name);
                        fd.append('turnoverTime', draft.turnoverTime);
                        fd.append('diaryColour', draft.diaryColour);
                        fd.append('notes', draft.notes || '');
                        fd.append('hours', JSON.stringify(draft.hours));
                        if (panel.unitId) fd.append('isActive', draft.isActive);
                        else fd.append('groupId', panel.groupId);
                        fd.append('existingImages', JSON.stringify(draft.images.map(img => ({ url: img.url, key: img.key }))));
                        draft.newFiles.forEach((f, i) => fd.append('images', f, `room-${i}.jpg`));
                        const ok = panel.unitId
                            ? await call('put', `${API.RESOURCE_UNIT}/${panel.unitId}`, fd)
                            : await call('post', API.RESOURCE_UNIT, fd);
                        if (!ok) return;
                        toast.success(panel.unitId ? 'Saved' : `${draft.name} added`);
                        if (panel.unitId) afterSave({ kind: 'unitView', unitId: panel.unitId }); else setPanel(null);
                    }} />
            )}
            {panel?.kind === 'removeUnit' && (
                <SlidePanel title={`Remove ${panel.unit.name}?`} onClose={() => setPanel(null)} width={480}
                    footer={
                        <div className="res-panel-footer">
                            <button type="button" className="sp-btn-secondary" onClick={() => setPanel(null)}>Keep it</button>
                            <button type="button" className="sp-btn-primary res-btn--danger" onClick={async () => {
                                const ok = await call('delete', `${API.RESOURCE_UNIT}/${panel.unit._id}`, { confirm: true });
                                if (ok) { toast.success(`${panel.unit.name} removed`); setPanel(null); }
                            }}>Remove anyway</button>
                        </div>
                    }>
                    <p className="res-confirm-lead">
                        {panel.count} upcoming booking{panel.count === 1 ? '' : 's'} {panel.count === 1 ? 'holds' : 'hold'} this room.
                    </p>
                    <p className="res-confirm-note">
                        They keep it, nothing about them changes. The room just stops being
                        offered to new bookings and disappears from the diary lanes.
                    </p>
                </SlidePanel>
            )}
            {panel?.kind === 'removeGroup' && (
                <SlidePanel title={`Remove ${panel.group.name}?`} onClose={() => setPanel(null)} width={480}
                    footer={
                        <div className="res-panel-footer">
                            <button type="button" className="sp-btn-secondary" onClick={() => setPanel(null)}>Keep the group</button>
                            <button type="button" className="sp-btn-primary res-btn--danger" onClick={async () => {
                                const ok = await call('delete', `${API.RESOURCE_GROUP}/${panel.group._id}`, { confirm: true });
                                if (ok) { toast.success(`${panel.group.name} removed`); setPanel(null); }
                            }}>Remove anyway</button>
                        </div>
                    }>
                    <p className="res-confirm-lead">
                        {panel.affected.length} service{panel.affected.length === 1 ? '' : 's'} need{panel.affected.length === 1 ? 's' : ''} this
                        room and will stop asking for one:
                    </p>
                    <ul className="res-confirm-list">
                        {panel.affected.map(sv => <li key={sv._id}>{sv.serviceName}</li>)}
                    </ul>
                    <p className="res-confirm-note">
                        Existing bookings keep the rooms they were given. Online booking for these
                        services goes back to stylist-only availability until they're re-pointed.
                    </p>
                </SlidePanel>
            )}
        </div>
    );
}

// ---- Read-only views (ServiceDetailPanel language: sdp- header, body, footer) ----

const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

function GroupViewPanel({ group, units, onClose, onEdit, onDelete, onOpenUnit, onAddUnit }) {
    const singular = singularOf(group.name).toLowerCase();
    const live = units.filter(u => u.isActive !== false);
    return (
        <SlidePanel onClose={onClose} className="sdp-panel res-view" width={420}>
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{group.name}</h2>
                    <span className="sdp-header-category">Resource group</span>
                </div>
                <button type="button" className="beu-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
            </div>
            <div className="sdp-body">
                <div className="sdp-stats-row">
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{units.length}</span>
                        <span className="sdp-stat-label">{units.length === 1 ? singular : group.name.toLowerCase()}</span>
                    </div>
                    <div className="sdp-stat-divider" />
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{live.length}</span>
                        <span className="sdp-stat-label">Bookable</span>
                    </div>
                </div>
                <div className="sdp-section">
                    <div className="sdp-section-header">
                        <p className="beu-section-label">In this group</p>
                        <button type="button" className="res-view-link" onClick={onAddUnit}>Add {singular}</button>
                    </div>
                    {units.length ? (
                        <div className="sdp-staff-list">
                            {units.map(u => (
                                <button type="button" className="sdp-staff-row res-view-row" key={u._id} onClick={() => onOpenUnit(u)}>
                                    {u.images?.[0]?.url
                                        ? <img className="res-unit-thumb" src={u.images[0].url} alt="" />
                                        : <span className="sdp-staff-dot" style={{ background: u.diaryColour || '#8B8B93' }} />}
                                    <span className="sdp-staff-name">{u.name}</span>
                                    <span className="sdp-staff-meta">{u.isActive === false ? 'Hidden' : u.turnoverTime > 0 ? `${u.turnoverTime}m reset` : 'No reset'}</span>
                                </button>
                            ))}
                        </div>
                    ) : <p className="sdp-description res-view-empty">No {group.name.toLowerCase()} yet. Add the first {singular} to start offering it to bookings.</p>}
                </div>
                <p className="sdp-description res-view-note">
                    Services ask for “any {singular}” from this group. Beu picks a free one for each booking.
                </p>
            </div>
            <div className="sdp-footer">
                <button type="button" className="sdp-btn sdp-btn--secondary" onClick={onDelete}>Delete</button>
                <button type="button" className="sdp-btn sdp-btn--primary" onClick={onEdit}>Edit group</button>
            </div>
        </SlidePanel>
    );
}

function UnitViewPanel({ unit, group, onClose, onEdit, onDelete }) {
    const singular = singularOf(group?.name);
    const images = Array.isArray(unit.images) ? unit.images : [];
    const custom = unit.hours?.mode === 'custom';
    const days = custom ? { ...DEFAULT_DAYS(), ...unit.hours.days } : null;
    return (
        <SlidePanel onClose={onClose} className="sdp-panel res-view" width={420}>
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{unit.name}</h2>
                    {group && <span className="sdp-header-category">{group.name}</span>}
                </div>
                <button type="button" className="beu-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
            </div>
            <div className="sdp-body">
                <div className="sdp-stats-row">
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{unit.turnoverTime > 0 ? `${unit.turnoverTime} min` : 'None'}</span>
                        <span className="sdp-stat-label">Reset time</span>
                    </div>
                    <div className="sdp-stat-divider" />
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{unit.isActive === false ? 'Hidden' : 'Bookable'}</span>
                        <span className="sdp-stat-label">New bookings</span>
                    </div>
                </div>

                {images.length > 0 && (
                    <div className="sdp-image-strip">
                        {images.slice(0, 4).map((img, i) => <div key={i} className="sdp-image-thumb"><img src={img.url || img} alt="" /></div>)}
                        {images.length > 4 && <div className="sdp-image-thumb sdp-image-thumb--more">+{images.length - 4}</div>}
                    </div>
                )}

                <div className="sdp-section">
                    <p className="beu-section-label">Details</p>
                    <div className="sdp-detail-grid">
                        <div className="sdp-detail-row">
                            <span className="sdp-detail-label">Diary colour</span>
                            <span className="sdp-detail-value"><span className="sdp-colour-swatch" style={{ background: unit.diaryColour || DEFAULT_COLOUR }} />{(unit.diaryColour || DEFAULT_COLOUR).toUpperCase()}</span>
                        </div>
                        <div className="sdp-detail-row">
                            <span className="sdp-detail-label">Opening hours</span>
                            <span className="sdp-detail-value">{custom ? 'Set hours' : "Follows your team's hours"}</span>
                        </div>
                        {unit.notes && (
                            <div className="sdp-detail-row">
                                <span className="sdp-detail-label">Notes</span>
                                <span className="sdp-detail-value res-view-notes">{unit.notes}</span>
                            </div>
                        )}
                    </div>
                </div>

                {custom && (
                    <div className="sdp-section">
                        <p className="beu-section-label">Hours</p>
                        <div className="sdp-detail-grid">
                            {DAYS.map(([k, label]) => (
                                <div className="sdp-detail-row" key={k}>
                                    <span className="sdp-detail-label">{label}</span>
                                    <span className={`sdp-detail-value${days[k].open ? '' : ' res-view-closed'}`}>
                                        {days[k].open ? `${fmtMin(days[k].start)} to ${fmtMin(days[k].end)}` : 'Closed'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="sdp-footer">
                <button type="button" className="sdp-btn sdp-btn--secondary" onClick={onDelete}>Delete</button>
                <button type="button" className="sdp-btn sdp-btn--primary" onClick={onEdit}>Edit {singular.toLowerCase()}</button>
            </div>
        </SlidePanel>
    );
}

function GroupPanel({ group, onClose, onSave }) {
    const [name, setName] = useState(group?.name || '');
    const canSave = !!name.trim() && (!group || name.trim() !== group.name);
    return (
        <SlidePanel title={group ? group.name : 'New group'} onClose={onClose} width={480}
            footer={
                <div className="res-panel-footer">
                    <button type="button" className="sp-btn-primary" disabled={!canSave} onClick={() => onSave(name.trim())}>{group ? 'Save' : 'Add group'}</button>
                </div>
            }>
            <div className="sep-field">
                <label className="sep-field-label">Group name</label>
                <input className="sep-input" autoFocus={!group} value={name} placeholder="e.g. Colour rooms, Basins"
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && canSave && onSave(name.trim())} />
                <p className="res-hint-block">
                    A group is what services ask for, such as “any colour room”. Add the actual rooms inside it;
                    Beu picks a free one for each booking.
                </p>
            </div>
        </SlidePanel>
    );
}

function UnitPanel({ groupName, unit, onClose, onSave }) {
    const singular = (groupName || 'unit').replace(/s$/, '');
    const [draft, setDraft] = useState({
        name: unit?.name || '',
        turnoverTime: snapReset(unit?.turnoverTime),
        diaryColour: unit?.diaryColour || DEFAULT_COLOUR,
        notes: unit?.notes || '',
        isActive: unit ? unit.isActive !== false : true,
        images: Array.isArray(unit?.images) ? unit.images : [],  // kept existing
        newFiles: [],                                             // freshly picked
        hours: unit?.hours?.mode === 'custom'
            ? { mode: 'custom', days: { ...DEFAULT_DAYS(), ...unit.hours.days } }
            : { mode: 'always', days: DEFAULT_DAYS() },
    });
    const setDay = (k, patch) => setDraft(d => ({
        ...d, hours: { ...d.hours, days: { ...d.hours.days, [k]: { ...d.hours.days[k], ...patch } } }
    }));
    // Copy the first open day's times onto every other open day.
    const applyToAll = () => setDraft(d => {
        const first = DAYS.map(([k]) => d.hours.days[k]).find(x => x.open);
        if (!first) return d;
        const days = {};
        DAYS.forEach(([k]) => { const x = d.hours.days[k]; days[k] = x.open ? { ...x, start: first.start, end: first.end } : x; });
        return { ...d, hours: { ...d.hours, days } };
    });
    const openDays = DAYS.filter(([k]) => draft.hours.days[k].open);
    const setReset = (n) => setDraft(d => ({ ...d, turnoverTime: snapReset(n) }));
    return (
        <SlidePanel title={unit ? unit.name : `New ${singular.toLowerCase()}`} onClose={onClose} width={480}
            footer={
                <div className="res-panel-footer">
                    <button type="button" className="sp-btn-primary" disabled={!draft.name.trim()} onClick={() => onSave(draft)}>
                        {unit ? 'Save' : `Add ${singular.toLowerCase()}`}
                    </button>
                </div>
            }>
            <div className="sep-field">
                <label className="sep-field-label">Name</label>
                <input className="sep-input" autoFocus={!unit} value={draft.name} placeholder={`e.g. ${singular} 1`}
                    onChange={e => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="sep-field">
                <label className="sep-field-label">Photos <span className="res-optional">(optional, up to 10)</span></label>
                <div className="res-photo-grid">
                    {draft.images.map((img, i) => (
                        <div className="res-photo-thumbwrap" key={`e${i}`}>
                            <img className="res-photo-thumb" alt="" src={img.url} />
                            <button type="button" className="res-photo-remove" aria-label="Remove photo"
                                onClick={() => setDraft(d => ({ ...d, images: d.images.filter((_, j) => j !== i) }))}>×</button>
                        </div>
                    ))}
                    {draft.newFiles.map((f, i) => (
                        <div className="res-photo-thumbwrap" key={`n${i}`}>
                            <img className="res-photo-thumb" alt="" src={URL.createObjectURL(f)} />
                            <button type="button" className="res-photo-remove" aria-label="Remove photo"
                                onClick={() => setDraft(d => ({ ...d, newFiles: d.newFiles.filter((_, j) => j !== i) }))}>×</button>
                        </div>
                    ))}
                    {(draft.images.length + draft.newFiles.length) < 10 && (
                        <label className="res-photo-add">
                            <input type="file" accept="image/*" multiple hidden
                                onChange={async (e) => {
                                    // NEVER reset the input mid-handler — iOS invalidates
                                    // the picked files (2026-08-29 lesson). Read first.
                                    const files = Array.from(e.target.files || []);
                                    if (!files.length) return;
                                    const room = 10 - draft.images.length - draft.newFiles.length;
                                    const picked = [];
                                    for (const f of files.slice(0, room)) {
                                        try { picked.push(await compressImage(f)); }
                                        catch (err) { if (!isHeicFile(f)) throw err; }
                                    }
                                    if (picked.length) setDraft(d => ({ ...d, newFiles: d.newFiles.concat(picked) }));
                                }} />
                            + Add
                        </label>
                    )}
                </div>
            </div>
            <div className="sep-section">
                <p className="sep-section-label">Diary Colour</p>
                <ColourPicker
                    value={draft.diaryColour}
                    onChange={(hex) => setDraft({ ...draft, diaryColour: hex })}
                    onReset={() => setDraft({ ...draft, diaryColour: DEFAULT_COLOUR })}
                    resetLabel="Reset to Default"
                />
            </div>
            <div className="sep-field">
                <label className="sep-field-label">Reset time</label>
                <div className="res-reset-row">
                    <div className="res-stepper" role="group" aria-label="Reset time in five minute steps">
                        <button type="button" className="res-stepper-btn" aria-label="Less reset time"
                            disabled={draft.turnoverTime <= 0} onClick={() => setReset(draft.turnoverTime - RESET_STEP)}>−</button>
                        <span className={`res-stepper-value${draft.turnoverTime ? '' : ' res-stepper-value--none'}`}>
                            {draft.turnoverTime ? `${draft.turnoverTime} min` : 'None'}
                        </span>
                        <button type="button" className="res-stepper-btn" aria-label="More reset time"
                            disabled={draft.turnoverTime >= RESET_MAX} onClick={() => setReset(draft.turnoverTime + RESET_STEP)}>+</button>
                    </div>
                    <span className="res-reset-unit">after each booking</span>
                </div>
                <p className="res-hint-block">
                    Time to clean and reset before the next client can use it. Blocks the
                    {' '}{singular.toLowerCase()}, while the stylist is free to take their next
                    appointment somewhere else.
                </p>
            </div>
            <div className="sep-field">
                <div className="sep-toggle-row">
                    <div className="sep-toggle-info">
                        <div className="sep-toggle-title">Set opening hours</div>
                        <div className="sep-toggle-desc">
                            {draft.hours.mode === 'custom'
                                ? 'Only bookable during these times.'
                                : "Off. Bookable whenever your team's hours allow."}
                        </div>
                    </div>
                    <Toggle checked={draft.hours.mode === 'custom'}
                        onChange={(on) => setDraft(d => ({ ...d, hours: { ...d.hours, mode: on ? 'custom' : 'always' } }))} />
                </div>
                {draft.hours.mode === 'custom' && (
                    <div className="res-hours">
                        <div className="res-hours-head">
                            <span className="res-hours-summary">
                                {openDays.length === 0 ? 'Closed every day' : openDays.length === 7 ? 'Open every day' : `Open ${openDays.length} day${openDays.length === 1 ? '' : 's'} a week`}
                            </span>
                            {openDays.length > 1 && (
                                <button type="button" className="res-hours-link" onClick={applyToAll}>Same hours every day</button>
                            )}
                        </div>
                        <div className="res-hours-grid">
                            {DAYS.map(([k, label]) => {
                                const d = draft.hours.days[k];
                                return (
                                    <div className={`res-hours-row${d.open ? '' : ' res-hours-row--off'}`} key={k}>
                                        <button type="button" className="res-hours-day" onClick={() => setDay(k, { open: !d.open })} aria-pressed={d.open}>
                                            <span className={`res-hours-check${d.open ? ' is-on' : ''}`} aria-hidden="true">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                                            </span>
                                            <span className="res-hours-daylabel">{label}</span>
                                        </button>
                                        {d.open ? (
                                            <span className="res-hours-times">
                                                <ShiftTimePicker label="Opens" placeholder="Opens"
                                                    selected={minToDate(d.start)}
                                                    onChange={(dt) => setDay(k, { start: dateToMin(dt) })} />
                                                <span className="res-hours-dash">to</span>
                                                <ShiftTimePicker label="Closes" placeholder="Closes"
                                                    selected={minToDate(d.end === 1440 ? 1439 : d.end)}
                                                    onChange={(dt) => setDay(k, { end: dateToMin(dt) })} />
                                            </span>
                                        ) : <span className="res-hours-closed">Closed</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            <div className="sep-field">
                <label className="sep-field-label">Notes <span className="res-optional">(optional)</span></label>
                <input className="sep-input" value={draft.notes} placeholder="e.g. no wheelchair access"
                    onChange={e => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            {unit && (
                <div className="sep-field">
                    <div className="sep-toggle-row">
                        <div className="sep-toggle-info">
                            <div className="sep-toggle-title">Available for new bookings</div>
                            <div className="sep-toggle-desc">Turn off to stop new bookings using it. History and upcoming bookings keep it.</div>
                        </div>
                        <Toggle checked={draft.isActive} onChange={(on) => setDraft({ ...draft, isActive: on })} />
                    </div>
                </div>
            )}
        </SlidePanel>
    );
}
